import { Center, Loader } from '@mantine/core';
import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { RequireAuth } from './auth/RequireAuth';
import { AppLayout } from './components/AppLayout';
import { ConfirmEmailPage } from './pages/ConfirmEmailPage';
import { ContentPageView } from './pages/ContentPageView';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { MyStudentsPage } from './pages/MyStudentsPage';
import { NewsDetailPage } from './pages/NewsDetailPage';
import { NewsFeedPage } from './pages/NewsFeedPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProfilePage } from './pages/ProfilePage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';

/**
 * Закрытые разделы грузятся отдельными частями, а Suspense для них стоит
 * в AppLayout. Гость приходит со страницы студии за новостями — ни редактор,
 * ни форма оформления абонемента ему не нужны, и платить за них ожиданием
 * первой отрисовки он не должен.
 */
const PaymentsPage = lazy(async () => ({
  default: (await import('./pages/PaymentsPage')).PaymentsPage,
}));
const SubscriptionCheckoutPage = lazy(async () => ({
  default: (await import('./pages/SubscriptionCheckoutPage')).SubscriptionCheckoutPage,
}));
const SubscriptionPage = lazy(async () => ({
  default: (await import('./pages/SubscriptionPage')).SubscriptionPage,
}));
const MySubscriptionsPage = lazy(async () => ({
  default: (await import('./pages/MySubscriptionsPage')).MySubscriptionsPage,
}));
const PaymentReturnPage = lazy(async () => ({
  default: (await import('./pages/PaymentReturnPage')).PaymentReturnPage,
}));
const SubscriptionPrintPage = lazy(async () => ({
  default: (await import('./pages/SubscriptionPrintPage')).SubscriptionPrintPage,
}));
const ServiceCheckoutPage = lazy(async () => ({
  default: (await import('./pages/ServiceCheckoutPage')).ServiceCheckoutPage,
}));
const ServiceOrderPage = lazy(async () => ({
  default: (await import('./pages/ServiceOrderPage')).ServiceOrderPage,
}));
const MyServicesPage = lazy(async () => ({
  default: (await import('./pages/MyServicesPage')).MyServicesPage,
}));
const ServiceOrderPrintPage = lazy(async () => ({
  default: (await import('./pages/ServiceOrderPrintPage')).ServiceOrderPrintPage,
}));

const AdminGroupsPage = lazy(async () => ({
  default: (await import('./pages/AdminGroupsPage')).AdminGroupsPage,
}));
const AdminStudentsPage = lazy(async () => ({
  default: (await import('./pages/AdminStudentsPage')).AdminStudentsPage,
}));
const AdminUsersPage = lazy(async () => ({
  default: (await import('./pages/AdminUsersPage')).AdminUsersPage,
}));
const AdminNewsPage = lazy(async () => ({
  default: (await import('./pages/AdminNewsPage')).AdminNewsPage,
}));
const AdminPagesPage = lazy(async () => ({
  default: (await import('./pages/AdminPagesPage')).AdminPagesPage,
}));
const AdminOutboxPage = lazy(async () => ({
  default: (await import('./pages/AdminOutboxPage')).AdminOutboxPage,
}));
const AdminSubscriptionsPage = lazy(async () => ({
  default: (await import('./pages/AdminSubscriptionsPage')).AdminSubscriptionsPage,
}));
const AdminPaymentsPage = lazy(async () => ({
  default: (await import('./pages/AdminPaymentsPage')).AdminPaymentsPage,
}));
const AdminReportPage = lazy(async () => ({
  default: (await import('./pages/AdminReportPage')).AdminReportPage,
}));
const AdminMonthReportPage = lazy(async () => ({
  default: (await import('./pages/AdminMonthReportPage')).AdminMonthReportPage,
}));
const AdminContractPage = lazy(async () => ({
  default: (await import('./pages/AdminContractPage')).AdminContractPage,
}));
const AdminServicesPage = lazy(async () => ({
  default: (await import('./pages/AdminServicesPage')).AdminServicesPage,
}));
const AdminServicesReportPage = lazy(async () => ({
  default: (await import('./pages/AdminServicesReportPage')).AdminServicesReportPage,
}));

export function App() {
  return (
    <Routes>
      {/* Печать идёт мимо каркаса приложения: на листе не нужны ни шапка,
          ни меню, ни подвал. Suspense здесь свой — тот, что в AppLayout,
          этот маршрут уже не накрывает. */}
      <Route element={<RequireAuth />}>
        <Route
          path="subscriptions/:id/print"
          element={
            <Suspense
              fallback={
                <Center h="100vh">
                  <Loader />
                </Center>
              }
            >
              <SubscriptionPrintPage />
            </Suspense>
          }
        />
        <Route
          path="services/orders/:id/print"
          element={
            <Suspense
              fallback={
                <Center h="100vh">
                  <Loader />
                </Center>
              }
            >
              <ServiceOrderPrintPage />
            </Suspense>
          }
        />
      </Route>

      <Route element={<AppLayout />}>
        <Route index element={<HomePage />} />
        <Route path="news" element={<NewsFeedPage />} />
        <Route path="news/:slug" element={<NewsDetailPage />} />
        <Route path="info" element={<ContentPageView slug="about" />} />
        <Route path="terms" element={<ContentPageView slug="terms" />} />
        <Route path="privacy" element={<ContentPageView slug="privacy" />} />
        <Route path="personal-data" element={<ContentPageView slug="personal-data" />} />

        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="confirm-email" element={<ConfirmEmailPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />

        <Route element={<RequireAuth />}>
          <Route path="profile" element={<ProfilePage />} />
          <Route path="students" element={<MyStudentsPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="payments/new/:studentId" element={<SubscriptionCheckoutPage />} />
          {/* Сюда ЮKassa возвращает родителя после оплаты. */}
          <Route path="payments/return" element={<PaymentReturnPage />} />
          <Route path="subscriptions" element={<MySubscriptionsPage />} />
          <Route path="subscriptions/:id" element={<SubscriptionPage />} />
          {/* Список идёт первым: иначе «orders» попало бы в :slug услуги. */}
          <Route path="services/orders" element={<MyServicesPage />} />
          <Route path="services/orders/:id" element={<ServiceOrderPage />} />
          <Route path="services/:slug" element={<ServiceCheckoutPage />} />
        </Route>

        {/* Разделы администратора закрыты и маршрутом, и проверкой на сервере:
            скрытый пункт меню сам по себе не защита. */}
        <Route element={<RequireAuth roles={['Admin']} />}>
          <Route path="admin/groups" element={<AdminGroupsPage />} />
          <Route path="admin/students" element={<AdminStudentsPage />} />
          <Route path="admin/users" element={<AdminUsersPage />} />
          <Route path="admin/news" element={<AdminNewsPage />} />
          <Route path="admin/subscriptions" element={<AdminSubscriptionsPage />} />
          <Route path="admin/payments" element={<AdminPaymentsPage />} />
          <Route path="admin/services" element={<AdminServicesPage />} />
          <Route path="admin/reports" element={<AdminReportPage />} />
          <Route path="admin/reports/services" element={<AdminServicesReportPage />} />
          <Route path="admin/month" element={<AdminMonthReportPage />} />
          <Route path="admin/outbox" element={<AdminOutboxPage />} />
          <Route path="admin/pages" element={<AdminPagesPage />} />
          <Route path="admin/contract" element={<AdminContractPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
